import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { loginWithGoogle } from 'redux/authSlice';
import { useGoogleLogin } from '@react-oauth/google';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProfileUser } from 'redux/profileUserSlice';

const SocialAuthButtons = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const from = location?.state?.from || "/";

  // const handleSignInAzure = () => {
  //   alert("Sign in with Microsoft Azure")
  // }

  // `.unwrap()` is what makes the catch below reachable. Without it a rejected
  // thunk still resolves, so a refused sign-in fell straight through to
  // getProfileUser() and navigate("/") — with no token, landing the user back
  // on this page with nothing said. Every reason the server gives (account not
  // found, rate limited, a validation failure on the record) was invisible, and
  // one person spent two weeks locked out of production because of it.
  const handleLogin = async (codeResponse) => {
    try {
      await dispatch(loginWithGoogle(codeResponse)).unwrap();

      // Best-effort, deliberately not unwrapped: the token is already stored,
      // so a hiccup fetching the profile must not turn a good sign-in into a
      // failure. MainLayout fetches it again on mount.
      await dispatch(getProfileUser());
      navigate(from);
    } catch (error) {
      // rejectWithValue hands back the server's message as a plain string.
      const message = typeof error === 'string'
        ? error
        : error?.message || 'An error occurred while signing in.';
      toast.error(message);
      // Deliberately no navigate(): staying put is what keeps the toast on
      // screen long enough to read.
    }
  };

  const onErrorCallback = (error) => {
    toast.error("Login failed.", error);
    throw new Error(error.message);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleLogin,
    onError: onErrorCallback
  });

  useEffect(() => {
    if (googleLogin.token) {
      handleLogin(googleLogin.codeResponse);
    }
    // only on a new token: handleLogin is recreated every render and would re-submit the login in a loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleLogin.token]);

  return (
    <div className="tw-mb-0">
      <div className="tw-mt-2">
        <button
          type="button"
          className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-w-full tw-bg-white tw-border tw-border-solid tw-border-slate-300 tw-rounded-md tw-text-slate-900 tw-py-2 tw-px-4 tw-font-medium tw-shadow-sm hover:tw-bg-slate-50 tw-cursor-pointer"
          onClick={googleLogin}
        >
          <svg className="tw-w-8 tw-h-8" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  ></path>
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  ></path>
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  ></path>
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  ></path>
                </svg>
          Sign in with Google
        </button>
      </div>
    </div>
  )
};

export default SocialAuthButtons;
